'use strict';

/**
 * The config arrays exported below contain objects used to find various information for a detail.
 *
 * Each object has
 * - a title key used to grab the title for a detail from an object from the static repo
 * - a contents key to grab detail data from an object created by ItemStore's getDetails method,
 * - a testName key to grab the testName mechaGodzilla looks for on a detail's markup during it's tests.
 *
 * Additionaly, an object may have
 *   - a gaLabel key if a detail has links that need to be tracked
 *   - a components key if a component other than DetailsItem.jsx should render a detail.
 *
 * Note that order matters. The order of the objects in the array determine the order the details
 * will appear on the pdp.
 */

const verticals = require('dibs-vertical-helpers');
const findNameSpace = require('find-namespace-value');
const detailsConstants = require('../constants/detailsConstants');

const sharedConfigs = {
    creator: {
        title: 'shared.creator',
        contents: 'creator',
        gaLabel: 'creator',
        testName: 'pdp-spec-creator'
    },
    periodOf: {
        title: 'shared.periodOf',
        contents: 'periodOf',
        gaLabel: 'style',
        testName: 'pdp-spec-of-the-period'
    },
    styleOf: {
        title: 'shared.styleOf',
        contents: 'styleOf',
        gaLabel: 'style',
        testName: 'pdp-spec-in-the-style-of'
    },
    placeOfOrigin: {
        title: 'shared.placeOfOrigin',
        contents: 'placeOfOrigin',
        gaLabel: 'origin',
        testName: 'pdp-spec-place-of-origin'
    },
    dateOfManufacture: {
        title: 'shared.dateOfManufacture',
        contents: 'dateOfManufacture',
        testName: 'pdp-spec-date-of-manufacture'
    },
    period: {
        title: 'shared.period',
        contents: 'period',
        gaLabel: 'period',
        testName: 'pdp-spec-period'
    },
    condition: {
        title: 'shared.condition',
        contents: 'condition',
        testName: 'pdp-spec-condition'
    },
    wear: {
        title: 'shared.wear',
        contents: 'wear',
        testName: 'pdp-spec-wear'
    },
    dimensions: {
        title: 'shared.dimensions',
        contents: 'dimensions',
        component: findNameSpace('components.dimensions', detailsConstants),
        testName: 'pdp-spec-dimensions'
    },
    diameter: {
        title: 'shared.diameter',
        contents: 'diameter',
        testName: 'pdp-spec-diameter'
    },
    length: {
        title: 'shared.length',
        contents: 'length',
        testName: 'pdp-spec-length'
    },
    weight: {
        title: 'shared.weight',
        contents: 'weight',
        testName: 'pdp-spec-weight'
    },
    dealerLocation: {
        title: 'shared.dealerLocation',
        contents: 'dealerLocation',
        testName: 'pdp-spec-dealer-location'
    },
    dealerReference: {
        title: detailsConstants.title.dealerReference,
        contents: 'dealerReference',
        testName: 'pdp-spec-dealer-reference'
    },
    quantity: {
        title: 'shared.quantity',
        contents: 'quantity',
        testName: 'pdp-spec-number-of-items'
    },
    referenceNumber: {
        title: 'shared.referenceNumber',
        contents: 'referenceNumber',
        testName: 'pdp-spec-reference-number'
    },
    itemLocation: {
        title: 'shared.itemLocation',
        contents: 'itemLocation',
        gaLabel: 'item location',
        component: findNameSpace('components.multiline', detailsConstants),
        testName: 'pdp-spec-item-location'
    }
};

// The order of these is the order in which they appear on the page
module.exports = {
    [verticals.VERTICAL_FURNITURE]: [
        sharedConfigs.creator,
        {
            title: 'furniture.productionType',
            contents: 'productionType',
            testName: 'pdp-spec-production-type'
        },
        {
            title: 'furniture.leadTime',
            contents: 'leadTime',
            testName: 'pdp-spec-lead-time'
        },
        sharedConfigs.periodOf,
        sharedConfigs.styleOf,
        sharedConfigs.placeOfOrigin,
        sharedConfigs.dateOfManufacture,
        sharedConfigs.period,
        {
            title: 'shared.materialsAndTechniques',
            contents: 'materialsAndTechniques',
            gaLabel: 'material',
            component: findNameSpace('components.materialsAndTechniques', detailsConstants),
            testName: 'pdp-spec-materials-and-techniques'
        },
        sharedConfigs.condition,
        sharedConfigs.wear,
        sharedConfigs.dimensions,
        sharedConfigs.diameter,
        sharedConfigs.weight,
        {
            title: 'furniture.seatHeight',
            contents: 'seatHeight',
            testName: 'pdp-spec-seat-height'
        },
        sharedConfigs.dealerLocation,
        sharedConfigs.dealerReference,
        sharedConfigs.itemLocation,
        sharedConfigs.quantity,
        sharedConfigs.referenceNumber
    ],
    [verticals.VERTICAL_JEWELRY]: [
        sharedConfigs.creator,
        sharedConfigs.periodOf,
        sharedConfigs.placeOfOrigin,
        sharedConfigs.dateOfManufacture,
        sharedConfigs.period,
        {
            title: 'jewelry.metal',
            contents: 'metal',
            gaLabel: 'metal',
            testName: 'pdp-spec-metal'
        },
        {
            title: 'jewelry.stone',
            contents: 'stone',
            gaLabel: 'stone',
            testName: 'pdp-spec-stone'
        },
        sharedConfigs.condition,
        sharedConfigs.wear,
        sharedConfigs.dimensions,
        sharedConfigs.diameter,
        sharedConfigs.length,
        sharedConfigs.weight,
        sharedConfigs.dealerLocation,
        sharedConfigs.dealerReference,
        sharedConfigs.itemLocation,
        sharedConfigs.quantity,
        sharedConfigs.referenceNumber
    ],
    [verticals.VERTICAL_ART]: [
        {
            title: 'art.artist',
            contents: 'creator',
            gaLabel: 'creator',
            testName: 'pdp-spec-artist'
        },
        {
            title: 'art.creationYear',
            contents: 'dateOfManufacture',
            testName: 'pdp-spec-creation-year'
        },
        sharedConfigs.placeOfOrigin,
        {
            title: 'art.medium',
            contents: 'medium',
            gaLabel: 'material',
            testName: 'pdp-spec-medium'
        },
        {
            title: 'art.movementAndStyle',
            contents: 'styleOf',
            gaLabel: 'style',
            testName: 'pdp-spec-movement-style'
        },
        sharedConfigs.condition,
        sharedConfigs.wear,
        sharedConfigs.dimensions,
        sharedConfigs.diameter,
        sharedConfigs.length,
        sharedConfigs.weight,
        {
            title: 'art.galleryLocation',
            contents: 'dealerLocation',
            testName: 'pdp-spec-gallery-location'
        },
        sharedConfigs.itemLocation,
        sharedConfigs.quantity,
        sharedConfigs.referenceNumber,
        sharedConfigs.dealerReference,
        {
            title: 'art.versions',
            contents: 'versions',
            component: findNameSpace('components.multiline', detailsConstants),
            testName: 'pdp-spec-more-editions-sizes'
        }
    ],
    [verticals.VERTICAL_FASHION]: [
        {
            title: 'fashion.creator',
            contents: 'creator',
            gaLabel: 'creator',
            testName: 'pdp-spec-creator'
        },
        {
            title: 'fashion.brand',
            contents: 'brand',
            testName: 'pdp-spec-brand'
        },
        sharedConfigs.styleOf,
        sharedConfigs.placeOfOrigin,
        sharedConfigs.period,
        {
            title: 'fashion.materialNotes',
            contents: 'materialNotes',
            testName: 'pdp-spec-materials-notes'
        },
        sharedConfigs.condition,
        sharedConfigs.wear,
        sharedConfigs.dimensions,
        {
            title: 'fashion.markedSize',
            contents: 'markedSize',
            testName: 'pdp-spec-size-as-marked'
        },
        {
            title: 'fashion.bust',
            contents: 'bust',
            testName: 'pdp-spec-bust'
        },
        {
            title: 'fashion.waist',
            contents: 'waist',
            testName: 'pdp-spec-waist'
        },
        {
            title: 'fashion.hip',
            contents: 'hip',
            testName: 'pdp-spec-hip'
        },
        sharedConfigs.length,
        {
            title: 'fashion.shoulderToHem',
            contents: 'shoulderToHem',
            testName: 'pdp-spec-shoulder-to-hem'
        },
        {
            title: 'fashion.inseam',
            contents: 'inseam',
            testName: 'pdp-spec-inseam'
        },
        sharedConfigs.diameter,
        sharedConfigs.weight,
        sharedConfigs.dealerLocation,
        sharedConfigs.dealerReference,
        sharedConfigs.itemLocation,
        sharedConfigs.quantity,
        sharedConfigs.referenceNumber
    ]
};



// WEBPACK FOOTER //
// ./assets/js/newPdp/details/detailsConfig.es.js